import * as Yup from "yup";
import { Formik } from "formik";
import { FC, useContext, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { AppContext } from "../../../../../appContext/AppContext";
import { IconBack } from "../../../../../component/IconSvg";
import { CODE, FORM_CODE } from "../../../../../utils/Constant";
import { formatDateDTO, validateNgay } from "../../../../../utils/FormatUtils";
import { QuanLyHopDongContext, QuanLyHopDongContextProps } from "../../../../components/tab-quan-ly-hop-dong/QuanLyHopDong";
import { addNhanVien, updateNhanVien } from "../../../../services/KhamDoanHopDongServices";
import { initialValuesThemHD } from "./constants/ThemNhanVien";
import FormThemMoiNhanVien from "./FormThemMoiNhanVien";
import { AddNhanVien } from "./models/ThemNhanVienModel";
import { useIntl } from "react-intl";

type Props = {
    show: boolean;
    onHide: () => void;
};

const ModelThemMoiNhanVien: FC<Props> = (props) => {
    const { show, onHide } = props;
    const intl = useIntl();
    const { dataEditNV, TTHopDong } = useContext<QuanLyHopDongContextProps>(QuanLyHopDongContext)
    const { setIsLoading } = useContext(AppContext);
    const [dataTienSuBanThan, setDataTienSuBanThan] = useState<any>([]);

    const validationSchema = Yup.object({
        name: Yup.string().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })),
        dobDay: Yup.number().typeError("Vui lòng nhập số")
            .nullable()
            .min(1, 'Ngày không hợp lệ')
            .max(31, 'Ngày không hợp lệ')
            .test('is-valid-date', 'Ngày không hợp lệ', function (value) {
                const { dobDay, dobMonth, dobYear } = this.parent;
                if (!dobMonth || !dobYear) return true;
                return validateNgay(dobDay, dobMonth, dobYear);
            }),
        dobMonth: Yup.number().typeError("Vui lòng nhập số")
            .nullable()
            .min(1, 'Tháng không hợp lệ')
            .max(12, 'Tháng không hợp lệ')
            .test('is-valid-month', 'Ngày không hợp lệ', function (value) {
                const { dobDay, dobMonth, dobYear } = this.parent;
                if (!dobYear) return true;
                return true;
            }),
        dobYear: Yup.number().typeError("Vui lòng nhập số")
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable()
            .min(1900, 'Năm lớn hơn hoặc bằng 1900')
            .max(new Date().getFullYear(), 'Năm nhỏ hơn hoặc bằng hiện tại')
            .test('is-valid-year', 'Ngày sinh không được lớn hơn ngày hiện tại', function (value) {
                const { dobDay, dobMonth, dobYear } = this.parent;
                if (!dobMonth) return true;
                if (dobDay) {
                    const inputDate = new Date(dobYear, dobMonth - 1, dobDay);
                    const today = new Date();
                    return inputDate <= today;
                }

                const today = new Date();
                if (dobYear > today.getFullYear() || (dobYear === today.getFullYear() && dobMonth && dobMonth > today.getMonth() + 1)) {
                    return false;
                }
                return true;
            }),
        examExpectedDate: Yup.date().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })),
        genderObj: Yup.object().nullable().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })),
        examPackage: Yup.object().nullable().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })),
      });

    const handleFormSubmit = async (values: AddNhanVien) => {
        const patientValues = values;
        const now = new Date();

        const obs = {
            familyMedicalHistory: values.medicalHistoryFamily,
            medicalHistory: values.medicalHistory,
        };
        const newDataTienSuBanThan = dataTienSuBanThan?.filter((item: any) => 
            item.diseaseName ||
            item.discoveryYear || 
            item.occDiseaseName || 
            item.occDiscoveryYear
        );
        patientValues.obs = obs;
        patientValues.termModelCode = FORM_CODE.SO_KHAM_SUC_KHOE;
        patientValues.personalMedicals = newDataTienSuBanThan;
        patientValues.patientCode = patientValues.patientCode ? patientValues.patientCode : null;
        patientValues.maritalStatusId = patientValues?.maritalStatus?.id || "";
        patientValues.contractId = TTHopDong?.id || "";
        patientValues.maritalStatusName = patientValues?.maritalStatus?.name || "";

        patientValues.divisionId = patientValues?.division?.id || "";
        patientValues.divisionName = patientValues?.division?.name || "";

        patientValues.gender = patientValues?.genderObj?.name || "";
        patientValues.phone = (patientValues?.phone?.toString().charAt(0) === '0') ? patientValues?.phone : `0${patientValues?.phone}` || "";

        patientValues.examPackageId = patientValues?.examPackage?.id || "";
        patientValues.examPackageName = patientValues?.examPackage?.name || "";

        patientValues.groupId = patientValues?.group?.id || "";
        patientValues.groupName = patientValues?.group?.name || ""; 
        patientValues.dobDay = patientValues?.dobDay || "";
        patientValues.dobMonth = patientValues?.dobMonth || "";
        patientValues.dobYear = patientValues?.dobYear || ""; 
        patientValues.issuingDate = formatDateDTO(patientValues?.issuingDate || ""); 
        patientValues.workStartDate = formatDateDTO(patientValues?.workStartDate || ""); 
        patientValues.examExpectedDate = formatDateDTO(patientValues?.examExpectedDate || ""); 

        try {
            setIsLoading(true);
            let { data } = (patientValues?.id || dataEditNV?.id)
                ? await updateNhanVien({
                    ...patientValues,
                    id: patientValues?.id || dataEditNV?.id
                })
                : await addNhanVien(patientValues);
            if (CODE.SUCCESS === data?.code) {
                toast.success(((patientValues?.id || dataEditNV?.id) ? "Cập nhật" : "Thêm") + " bệnh nhân thành công");
                onHide()
            } else {
                let errorMesage = data?.message || "Xảy ra lỗi, vui lòng thử lại!";
                toast.warning(errorMesage);
            }
        } catch (error) {
            // toast.warning("Xảy ra lỗi, vui lòng thử lại!");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <Formik<AddNhanVien>
            initialValues={{ ...initialValuesThemHD }}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
        >
            {({
                handleSubmit,
            }) => (
                <Form
                    onSubmit={handleSubmit}
                >
                    <div className="receive overflow-auto" id="form-them-moi-hop-dong" >
                        <Modal
                            centered
                            show={show}
                            onHide={onHide}
                            size="xl"
                        >
                            <Modal.Header className="py-3 header-modal" closeButton>
                                <div className="d-flex gap-6">
                                    <Modal.Title >
                                        <div className="text-transform-none fs-3">{`${!dataEditNV?.id ? 'Thêm mới' : 'Cập nhật'} nhân viên`}</div>
                                    </Modal.Title>
                                </div>
                            </Modal.Header>

                            <Modal.Body className="py-10 spaces px-5 bg-white">
                                <FormThemMoiNhanVien thongTinHD={TTHopDong} dataTienSuBanThan={dataTienSuBanThan} setDataTienSuBanThan={setDataTienSuBanThan}/>
                            </Modal.Body >
                            <Modal.Footer>
                                <Button type="submit" className="min-w-60px btn-fill" onClick={() => handleSubmit()}>
                                    Lưu
                                </Button>
                            </Modal.Footer>
                        </Modal >
                    </div>
                </Form>
            )}
        </Formik >
    );
};
export default ModelThemMoiNhanVien;

